import React, { useCallback } from "react";

import { BusinessGoalField } from "./BusinessGoalField";
import { ExperimentContextField } from "./ExperimentContextField";
import { ExperimentNameField } from "./ExperimentNameField";
import { SupportingMaterialsSection } from "./SupportingMaterialsSection";
import { TargetAudienceField } from "./TargetAudienceField";

import type {
  ExperimentFormData,
  FieldStatus,
  ValidationErrors,
} from "../../types/experiment.types";

interface ExperimentDetailsFormProps {
  experimentData: ExperimentFormData;
  setExperimentData: (updater: (prev: ExperimentFormData) => ExperimentFormData) => void;
  errors: ValidationErrors;
  validateField: (field: string, value: string) => void;
  getFieldStatus: (value: string) => FieldStatus;
  completionPercentage: number;
  nextRecommendedFields: string[];
  onFieldHelp?: (fieldType: string) => void;
  isCommentMode?: boolean;
  hideHeader?: boolean;
}

export const ExperimentDetailsForm: React.FC<ExperimentDetailsFormProps> = ({
  experimentData,
  setExperimentData,
  errors,
  validateField,
  getFieldStatus,
  onFieldHelp,
}) => {
  const [touchedFields, setTouchedFields] = React.useState<
    Record<string, boolean>
  >({});

  const markFieldTouched = useCallback((fieldKey: string) => {
    setTouchedFields((previous) =>
      previous[fieldKey] ? previous : { ...previous, [fieldKey]: true },
    );
  }, []);

  const handleNameChange = useCallback(
    (value: string) => {
      markFieldTouched("name");
      setExperimentData((prev) => ({ ...prev, name: value }));
      validateField("name", value);
    },
    [markFieldTouched, setExperimentData, validateField],
  );

  const fieldConfigs = [
    {
      id: "name",
      title: "Experiment name",
      description: "Choose a clear, descriptive name for your experiment",
      dependsOn: [] as string[],
      isCompleted: (data: ExperimentFormData) =>
        getFieldStatus(data.name) === "complete",
      component: (
        <ExperimentNameField
          value={experimentData.name || ""}
          onChange={handleNameChange}
          error={touchedFields.name ? errors.name : undefined}
          getFieldStatus={getFieldStatus}
          onFieldHelp={onFieldHelp}
        />
      ),
    },
    {
      id: "goal",
      title: "Business goal",
      description: "Define the primary business objective",
      dependsOn: ["name"],
      isCompleted: (data: ExperimentFormData) =>
        getFieldStatus(data.goal) === "complete",
      component: (
        <BusinessGoalField
          value={experimentData.goal || ""}
          onChange={(value) => {
            markFieldTouched("goal");
            setExperimentData((prev) => ({ ...prev, goal: value }));
            validateField("goal", value);
          }}
          error={touchedFields.goal ? errors.goal : undefined}
          getFieldStatus={getFieldStatus}
          onFieldHelp={onFieldHelp}
        />
      ),
    },
    {
      id: "description",
      title: "Context & background",
      description: "Provide detailed context about your experiment",
      dependsOn: ["goal"],
      isCompleted: (data: ExperimentFormData) =>
        getFieldStatus(data.description) === "complete",
      component: (
        <ExperimentContextField
          value={experimentData.description || ""}
          onChange={(value) => {
            markFieldTouched("description");
            setExperimentData((prev) => ({ ...prev, description: value }));
            validateField("description", value);
          }}
          error={touchedFields.description ? errors.description : undefined}
          getFieldStatus={getFieldStatus}
          isRecording={false}
          isSummarizing={false}
          canSummarize={
            !!experimentData.description &&
            experimentData.description.length > 50
          }
          onVoiceRecord={() => {}}
          onSummarize={() => {}}
          onFieldHelp={onFieldHelp}
        />
      ),
    },
    {
      id: "targetAudience",
      title: "Target audience",
      description: "Describe who this experiment is for",
      dependsOn: ["description"],
      isCompleted: (data: ExperimentFormData) =>
        (data.targetAudience || "").trim().length >= 20,
      component: (
        <TargetAudienceField
          value={experimentData.targetAudience || ""}
          onChange={(value) => {
            setExperimentData((prev) => ({ ...prev, targetAudience: value }));
          }}
        />
      ),
    },
    {
      id: "supportingMaterials",
      title: "Supporting materials",
      description: "Optional documentation to enhance AI analysis",
      dependsOn: ["description"],
      isCompleted: (data: ExperimentFormData) =>
        (data.supportingDocs?.length ?? 0) > 0 ||
        (data.supportingDocFiles?.length ?? 0) > 0,
      component: (
        <SupportingMaterialsSection
          experimentData={experimentData}
          setExperimentData={setExperimentData}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex-1 space-y-24">
        <div
          className="space-y-8"
          data-commentable
          data-component-id="experiment-details-section"
        >
          {fieldConfigs.map((config) => (
            <div
              key={config.id}
              data-commentable
              data-component-id={config.id}
              className="rounded-lg border border-white/10 bg-black p-6"
            >
              {config.component}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
